import { defineStore } from 'pinia'
import { useFirestore } from 'vuefire'
import {
  collection,
  onSnapshot,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore'
import { logger } from '@/utils/logger'
import type { ActionRegister } from '@/stores/chatbot'

/** One row of a delta preview — the row as it would be after applying. */
export interface RegisterDeltaRow {
  identity: string
  cells: Record<string, string | null>
  /** UPDATE only: the column keys whose value changes. */
  changed: string[]
  source: string
}

/** What applying a proposal would do. Counts are exact; the row lists are capped samples. */
export interface RegisterDelta {
  added_count: number
  updated_count: number
  removed_count: number
  unchanged_count: number
  added: RegisterDeltaRow[]
  updated: RegisterDeltaRow[]
  removed: RegisterDeltaRow[]
  dropped_columns: string[]
  schema_changed: boolean
  /** 'growth_over_threshold' | 'shrink_over_threshold' | 'register_emptied' */
  guardrail_flags: string[]
}

export interface RegisterProposal {
  id: string
  register_key: ActionRegister
  mode: 'additive' | 'destructive'
  status: 'pending' | 'approved' | 'rejected' | 'superseded'
  delta: RegisterDelta
  proposed_table?: { columns?: { key: string; label: string }[] }
  assessment_id: string
}

const EMPTY_DELTA: RegisterDelta = {
  added_count: 0,
  updated_count: 0,
  removed_count: 0,
  unchanged_count: 0,
  added: [],
  updated: [],
  removed: [],
  dropped_columns: [],
  schema_changed: false,
  guardrail_flags: [],
}

/** The memory types a staged proposal is stored under, one per register. */
const PROPOSAL_TYPES = [
  'qms_capa_list_proposal',
  'qms_nc_list_proposal',
  'qms_supplier_list_proposal',
  'qms_change_control_list_proposal',
] as const

/**
 * MYAC-883 — register re-extractions waiting for the user's approval.
 *
 * DORMANT since the reactive revert (chatbot#87): a chat-driven refresh writes the
 * register directly again, so no new proposals are staged. The staged engine stays in
 * the backend, and this store keeps live-watching PENDING proposals so one staged
 * before the revert (or by a future scoped flow) still gets its delta card and can be
 * resolved — including from another session or after a reload.
 *
 * The proposal is a `ProjectMemory` of its own type (never the register's, so the
 * register page can never render an unapproved table) — which also means it is read
 * under the rule that already covers project memories, with no new rule to deploy.
 *
 * Queries `type in [...]` only: an equality-only, single-field query needs no composite
 * index (the `type` + `created_at` composite is missing in dev and falls back to a
 * scan). Status and recency are filtered in memory — a project holds a handful.
 */
export const useRegisterProposalsStore = defineStore('registerProposals', {
  state: () => ({
    proposals: [] as RegisterProposal[],
    _pid: null as string | null,
    _unsub: null as Unsubscribe | null,
  }),
  getters: {
    /** The pending proposal for a register, if any. */
    pendingFor:
      state =>
      (register: ActionRegister): RegisterProposal | null =>
        state.proposals.find(p => p.register_key === register) ?? null,
  },
  actions: {
    subscribe(projectId: string): void {
      if (this._pid === projectId && this._unsub) return
      this.unsubscribe()
      this._pid = projectId
      const db = useFirestore()
      const q = query(
        collection(db, `projects/${projectId}/memories`),
        where('type', 'in', [...PROPOSAL_TYPES]),
      )
      this._unsub = onSnapshot(
        q,
        snapshot => {
          this.proposals = snapshot.docs
            .map(doc => {
              const data = (doc.data()?.structured_data ?? {}) as Partial<RegisterProposal>
              return {
                id: doc.id,
                register_key: (data.register_key ?? 'capa') as ActionRegister,
                mode: data.mode ?? 'additive',
                status: data.status ?? 'pending',
                // Tolerate a proposal written by a newer/older backend: a missing delta
                // renders as "no changes" rather than crashing the conversation.
                delta: { ...EMPTY_DELTA, ...(data.delta ?? {}) },
                proposed_table: data.proposed_table,
                assessment_id: data.assessment_id ?? '',
              }
            })
            // Superseded / approved / rejected versions stay in history — only a
            // pending one is still a question for the user.
            .filter(p => p.status === 'pending')
        },
        error => logger.error('register proposals subscription failed', error),
      )
    },
    unsubscribe(): void {
      this._unsub?.()
      this._unsub = null
      this._pid = null
      this.proposals = []
    },
  },
})
